import { useActiveProject } from "@/features/project/ActiveProject";
import { isValidEmail } from "@/utils/isValidEmail";
import { useDraftOrderSearchByEmail, useDraftOrderSearchById } from "@repo/api";
import { t } from "@repo/translation";
import { DebouncedSearchField, useSettings } from "@repo/ui";
import { SectionContainer } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo } from "react";

import { SearchResultHandler } from "../../components/SearchResultHandler";
import { isOrderNumber } from "../../hooks/searchOrder";
import {
  type AddDraftOrderState,
  type AddDraftOrderStateActions,
} from "./AddDraftOrderReducer";

interface SearchStepProps {
  actions: AddDraftOrderStateActions;
  state: AddDraftOrderState;
}

export const SearchCustomerDraftOrders = ({
  actions,
  state,
}: SearchStepProps) => {
  const { market } = useSettings();
  const { opportunity } = useActiveProject();
  const { searchValue } = state;
  const isSearchOnOrder = isOrderNumber(searchValue);
  const isSearchOnEmail = isValidEmail(searchValue);

  const searchParams = useMemo(() => {
    if (isSearchOnOrder) {
      return { searchOrder: searchValue, searchEmail: undefined };
    } else if (isSearchOnEmail) {
      return { searchOrder: undefined, searchEmail: searchValue };
    }
    return { searchOrder: undefined, searchEmail: undefined };
  }, [searchValue, isSearchOnOrder, isSearchOnEmail]);

  const { data: draftOrderData, isValidating: isOrderSearchValidating } =
    useDraftOrderSearchById(searchParams.searchOrder);
  const { data: draftOrderDataByEmail, isValidating: isEmailSearchValidating } =
    useDraftOrderSearchByEmail(
      market,
      opportunity?.customerType,
      searchParams.searchEmail
    );

  const isValidating = isOrderSearchValidating || isEmailSearchValidating;

  useEffect(() => {
    const draftData = draftOrderDataByEmail || draftOrderData;
    if (!isEmpty(draftData)) {
      actions.setDraftOrders(draftData);
    } else if (!isValidating && searchValue) {
      actions.setDraftOrders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, draftOrderDataByEmail, draftOrderData]);

  return (
    <SectionContainer>
      <DebouncedSearchField
        id="seach-draft-order-input"
        placeholder={t("prompt.placeholder_search_draft_order")}
        debounceTime={700}
        onChange={(term) => {
          actions.setSearchValue(term === "" ? undefined : term);
          trackAnalytics("search_with_email_or_draft_order_number");
        }}
      />
      <SearchResultHandler
        isValidating={isValidating}
        isNoData={isEmpty(draftOrderData) && isEmpty(draftOrderDataByEmail)}
        searchValue={searchValue}
      />
    </SectionContainer>
  );
};
